import React, { useState, useRef, useEffect } from 'react';
import {
    Card,
    Row,
    Col,
    Button,
    Modal,
    List,
    Spin,
    Empty,
    Select,
    Typography,
    Space,
    Divider,
    message,
} from 'antd';
import {
    HistoryOutlined,
    DownloadOutlined,
    SearchOutlined,
    ReloadOutlined,
    DeleteOutlined,
    LoadingOutlined,
} from '@ant-design/icons';
import { HotTable } from '@handsontable/react-wrapper';
import { registerAllModules } from 'handsontable/registry';
import 'handsontable/styles/handsontable.css';
import 'handsontable/styles/ht-theme-main.css';
import './KeywordRankVolumeCheck.css';
import { apiErrToMessage } from '../../utils/apiError';
import AuthService from '../../services/authService';
import { dispatchQuotaUpdated } from '../../utils/quotaEvent';
import ProtectedMenuPage from '../../components/ProtectedMenuPage/ProtectedMenuPage';
import KakaoFloatButton from '../../components/KakaoFloatButton/KakaoFloatButton';
import * as XLSX from 'xlsx';
import { v4 as uuidv4 } from 'uuid';
import '../FindNaverRankTracking/FindNaverRankTracking.css';
import '../../styles/hot-table-status-pills.css';
import { hotStatusPillRenderer, plainStatusForExport } from '../../utils/hotTableStatusRenderer';

registerAllModules();
const COL_WIDTHS_STORAGE_KEY = 'keywordRankVolumeCheck_colWidths';
const DEFAULT_COL_WIDTHS = [
    120, 180, 180, 130, 150, 130, 100, 120, 150, 150, 120, 160, 170, 170, 220,
];

const loadSavedColWidths = () => {
    try {
        const saved = localStorage.getItem(COL_WIDTHS_STORAGE_KEY);
        if (!saved) return null;
        const parsed = JSON.parse(saved);
        return Array.isArray(parsed) && parsed.length === DEFAULT_COL_WIDTHS.length ? parsed : null;
    } catch {
        return null;
    }
};

const saveColWidths = widths => {
    try {
        localStorage.setItem(COL_WIDTHS_STORAGE_KEY, JSON.stringify(widths));
    } catch (e) {
        console.warn('colWidths 저장 실패:', e);
    }
};

const isKeywordRankDoneStatus = s => {
    const t = String(s ?? '').trim();
    return t === '완료' || t === '✅ 완료';
};

/* -----------------------------
   🔸 유틸 함수들
--------------------------------*/
function isValidMid(value = '') {
    if (!value) return false;
    // URL 끝에 붙은 숫자 추출
    const match = String(value)
        .trim()
        .match(/(\d+)(?:\/)?$/);
    return !!match; // 끝에 숫자만 있으면 true
}

function _applyQuotaIfAny(setQuota, obj) {
    if (!obj || typeof obj !== 'object') return;
    const { used = 0, limit = 0, bonus = 0, remaining = 0, day = '' } = obj;
    setQuota(q => ({ ...q, used, limit, bonus, remaining, day }));
}

/* -----------------------------
   🔸 메인 컴포넌트
--------------------------------*/
const KeywordRankVolumeCheck = () => {
    const hotRef = useRef(null);
    const [loading, setLoading] = useState(false);

    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [historyData, setHistoryData] = useState([]);
    const [historyLoaded, setHistoryLoaded] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [offset, setOffset] = useState(0);
    const [fetching, setFetching] = useState(false);

    const PAGE_SIZE = 10;
    const [maxRows, setMaxRows] = useState(20);
    const [limitRank, setLimitRank] = useState(() => {
        const saved = localStorage.getItem('keywordRankVolume_limitRank');
        return saved ? Number(saved) : 10;
    });

    const [separatorType, setSeparatorType] = useState(() => {
        const saved = localStorage.getItem('keywordRankVolume_separator');
        return saved || ',';
    });

    useEffect(() => {
        localStorage.setItem('keywordRankVolume_limitRank', limitRank);
    }, [limitRank]);

    useEffect(() => {
        localStorage.setItem('keywordRankVolume_separator', separatorType);
    }, [separatorType]);

    // ✅ 공통 헤더 정의
    const colHeaders = [
        '키워드 <span class="text-danger">*</span>',
        '판매처URL <span class="text-danger">*</span>',
        '원부URL <span class="text-primary">*</span>',
        '포함키워드 <span class="text-primary">*</span>',
        '키워드모음',
        '판매처MID',
        '원부MID',
        '키워드순위',
        '생존키워드',
        '탈락키워드',
        '생존키워드수',
        '생존키워드검색량',
        '생존키워드통검순위',
        '생존키워드AD통검순위',
        '상태',
    ];

    const [tableData, setTableData] = useState([]);
    const [colWidths, setColWidths] = useState(() => {
        const saved = loadSavedColWidths();
        return saved ? [...saved] : [...DEFAULT_COL_WIDTHS];
    });

    /* -----------------------------
       🔹 초기 로드: 행 제한 가져오기
    --------------------------------*/
    useEffect(() => {
        (async () => {
            try {
                const { data } = await AuthService.authAxios.get('/settings/rank-limit', {
                    params: { gubun: 'keywordRankVolumeCheck' },
                });
                const rows = data?.success ? data.max_rows : 20;
                setMaxRows(rows);
                setTableData(Array.from({ length: rows }, () => Array(colHeaders.length).fill('')));
                console.log(`✅ MAX_ROWS = ${rows} (${data.source || 'default'})`);
            } catch {
                setMaxRows(20);
                setTableData(Array.from({ length: 20 }, () => Array(colHeaders.length).fill('')));
            }
        })();
    }, [colHeaders.length]);

    /* -----------------------------
       🔹 사용량(Quota) 상태 조회
    --------------------------------*/
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const { data } = await AuthService.authAxios.get(
                    '/naver/quota/status/keywordRankVolumeCheck',
                    { params: { ensureRow: 1 } }
                );
                if (!alive) return;
                if (data?.ok) {
                    // setQuota removed as per unused var fix
                }
            } catch (err) {
                console.error('Quota fetch error:', err);
            }
        })();
        return () => {
            alive = false;
        };
    }, []);

    /* -----------------------------
       🔹 상품 MID 조회
    --------------------------------*/
    const handleSearch = async () => {
        if (loading) return;
        setLoading(true);

        const sessionId = uuidv4();

        try {
            const currentData = [...tableData];
            const hot = hotRef.current.hotInstance;

            // ✅ 공통 에러 코드 매핑
            const errMap = {
                missing_url1: 'URL1이 누락되었습니다.',
                url1_is_catalog: 'URL1이 catalog 주소입니다.',
                invalid_url_format: 'URL 형식이 잘못되었습니다. (숫자로 끝나야 함)',
                missing_seller_title: '상품 제목을 찾지 못했습니다.',
                missing_catalog_title: '카탈로그 제목이 없습니다.',
                missing_both_titles: '상품 제목이 모두 누락되었습니다.',
            };

            // ✅ 공통 에러 핸들러
            const handleInvalidPairs = invalidPairs => {
                invalidPairs.forEach(err => {
                    const idx = err.index ?? 0;
                    const errType = err.error || 'unknown_error';
                    const friendly = errMap[errType] || `알 수 없는 오류 (${errType})`;
                    const errorUrl = err.url2 || err.url1 || '';
                    currentData[idx][14] = `❌ ${friendly}\n(${errorUrl})`;
                    hot.setCellMeta(idx, 14, 'className', 'error-row');
                });
                hot.render();
            };

            // ✅ 정상 행 필터링
            const validProducts = currentData
                .map((row, idx) => {
                    const keyword = row[0]?.trim();
                    const url1 = row[1]?.trim();
                    const url2 = row[2]?.trim();
                    const includeKeywords = row[3]?.trim();
                    const status = row[14]?.trim();

                    // ✅ 이미 완료된 행은 스킵
                    if (isKeywordRankDoneStatus(status)) {
                        return null;
                    }

                    // ❌ 실패 or 빈 값만 조회
                    if (keyword && ((url1 && isValidMid(url1)) || (url2 && isValidMid(url2)))) {
                        return { index: idx, keyword, url1, url2, includeKeywords };
                    }
                    return null;
                })
                .filter(Boolean);

            // 🔥 현재 완료된 행 개수
            const completedCount = currentData.filter(row =>
                isKeywordRankDoneStatus(row[14])
            ).length;

            // 🔥 조회할 수 있는 행이 없음
            if (validProducts.length === 0) {
                if (completedCount > 0) {
                    message.success('모든 항목이 이미 완료되었습니다.');
                } else {
                    message.warning('키워드 + 판매처URL 모두 입력한 행이 필요합니다.');
                }
                setLoading(false);
                return;
            }

            console.log(`🔍 총 ${validProducts.length}개 항목 순차 조회 시작`);

            const totalCount = validProducts.length;

            for (let reqIndex = 0; reqIndex < totalCount; reqIndex++) {
                const product = validProducts[reqIndex];
                const rowIndex = product.index;

                // 상태 표시
                currentData[rowIndex][14] = '<div class="loading-spinner"></div>';
                hot.setCellMeta(rowIndex, 14, 'className', 'processing-row');
                hot.render();

                try {
                    // 🔥 서버에 전달: sessionId + totalCount + currentIndex + products
                    const response = await AuthService.authAxios.post(
                        '/product2/keywordRankVolumeCheck',
                        {
                            sessionId,
                            totalCount,
                            currentIndex: reqIndex + 1, // 1부터 시작
                            products: [product],
                            limitRank: limitRank,
                            separator: separatorType,
                        }
                    );

                    const result = response.data;

                    // invalidPairs 처리
                    if (Array.isArray(result.invalidPairs) && result.invalidPairs.length > 0) {
                        handleInvalidPairs(result.invalidPairs);
                        continue;
                    }

                    // applyQuotaIfAny(setQuota, result.quota); // Removed unused
                    if (result.quota) dispatchQuotaUpdated('keywordRankVolumeCheck');

                    if (result.success) {
                        const item = result.product;
                        currentData[rowIndex][4] = item.keywordList;
                        currentData[rowIndex][5] = result.mids.sellerMid || '';
                        currentData[rowIndex][6] = result.mids.catalogMid || '';
                        currentData[rowIndex][7] = result.allKeywordRanks;
                        currentData[rowIndex][8] = result.survivalKeywordList;
                        currentData[rowIndex][9] = result.droppedKeywordList;
                        currentData[rowIndex][10] = result.survivalKeywordCount;
                        currentData[rowIndex][11] = result.survivalKeywordVolumes;
                        currentData[rowIndex][12] = result.integratedRanks;
                        currentData[rowIndex][13] = result.integratedAdRanks;
                        currentData[rowIndex][14] = '완료';

                        hot.setCellMeta(rowIndex, 14, 'className', 'completed-row');
                    } else {
                        currentData[rowIndex][14] = '❌ 처리 실패 (유효한 상품이 없습니다.)';
                        hot.setCellMeta(rowIndex, 14, 'className', 'error-row');
                    }

                    hot.render();
                } catch (err) {
                    const data = err?.response?.data;

                    // invalidPairs 처리
                    if (Array.isArray(data?.invalidPairs) && data.invalidPairs.length > 0) {
                        handleInvalidPairs(data.invalidPairs);
                    } else {
                        const msg = data?.message || err?.message || '서버 연결 실패';
                        currentData[rowIndex][14] = `❌ 요청 실패 (${msg})`;
                        hot.setCellMeta(rowIndex, 14, 'className', 'error-row');
                    }

                    hot.render();
                }

                await new Promise(res => setTimeout(res, 500));
            }

            setTableData([...currentData]);
            console.log('✅ 모든 요청 완료');
        } catch (err) {
            const status = err?.response?.status;
            const data = err?.response?.data;
            message.error(apiErrToMessage(data, status));
        } finally {
            setLoading(false);
        }
    };

    /* -----------------------------
       🔹 조회 기록
    --------------------------------*/
    const fetchHistory = async (nextOffset = 0, append = false) => {
        if (fetching) return;
        setFetching(true);
        try {
            const res = await AuthService.authAxios.get('/log/history', {
                params: { gubun: 'keywordRankVolumeCheck', offset: nextOffset },
            });

            if (res.data.success) {
                const newData = res.data.data;
                setHasMore(newData.length >= PAGE_SIZE);
                setHistoryData(append ? [...historyData, ...newData] : newData);
                setOffset(nextOffset + PAGE_SIZE);
            }
        } catch (err) {
            console.error('조회 기록 불러오기 실패:', err);
        } finally {
            setHistoryLoaded(true);
            setFetching(false);
        }
    };

    const handleDelete = async id => {
        Modal.confirm({
            title: '조회 기록 삭제',
            content: '이 기록을 삭제하시겠습니까?',
            okText: '삭제',
            okType: 'danger',
            cancelText: '취소',
            onOk: async () => {
                try {
                    await AuthService.authAxios.post('/log/delete', { id });
                    setHistoryData(prev => prev.filter(item => item.id !== id));
                    message.success('삭제되었습니다.');
                } catch (err) {
                    console.error('삭제 실패:', err);
                    message.error('삭제에 실패했습니다.');
                }
            },
        });
    };

    /* -----------------------------
       🔹 엑셀 다운로드
    --------------------------------*/
    const handleExportExcel = () => {
        try {
            const headers = colHeaders.map(h => h.replace(/<[^>]+>/g, '')); // HTML 제거
            const filteredData = tableData.filter(row => row.some(cell => cell && cell !== ''));
            if (filteredData.length === 0) return message.warning('저장할 결과가 없습니다.');

            const statusColIndex = colHeaders.length - 1;
            const exportData = filteredData.map(row =>
                Object.fromEntries(
                    headers.map((header, i) => [
                        header,
                        i === statusColIndex ? plainStatusForExport(row[i]) : row[i] || '',
                    ])
                )
            );

            const worksheet = XLSX.utils.json_to_sheet(exportData, { header: headers });
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, '조회결과');

            const now = new Date();
            const filename = `통합작업키워드추출_${now.toISOString().slice(0, 19).replace(/[-T:]/g, '')}.xlsx`;
            XLSX.writeFile(workbook, filename);
        } catch (err) {
            console.error('❌ 엑셀 다운로드 오류:', err);
            message.error('엑셀 다운로드 중 오류가 발생했습니다.');
        }
    };

    const handleReset = () => {
        setTableData(Array.from({ length: maxRows }, () => Array(colHeaders.length).fill('')));
    };

    const { Text } = Typography;

    return (
        <ProtectedMenuPage menuKey="keywordRankVolumeCheck">
            <div className="keyword-rank-volume-check-page">
                <div className="page-header-container no-header-border">
                    <Card className="control-card" bodyStyle={{ padding: '8px 16px' }}>
                        <Row justify="space-between" align="middle" gutter={16}>
                            <Col>
                                <Space size="small" wrap>
                                    <Space>
                                        <Text strong>순위 필터:</Text>
                                        <Select
                                            value={limitRank}
                                            onChange={setLimitRank}
                                            style={{ width: '120px' }}
                                        >
                                            <Select.Option value={5}>5위 이내</Select.Option>
                                            <Select.Option value={10}>10위 이내</Select.Option>
                                            <Select.Option value={100}>100위 이내</Select.Option>
                                        </Select>
                                    </Space>
                                    <Space>
                                        <Text strong>출력 구분자:</Text>
                                        <Select
                                            value={separatorType}
                                            onChange={setSeparatorType}
                                            style={{ width: '120px' }}
                                        >
                                            <Select.Option value=",">콤마 (,)</Select.Option>
                                            <Select.Option value="|">파이프 (|)</Select.Option>
                                            <Select.Option value="/">슬래시 (/)</Select.Option>
                                        </Select>
                                    </Space>
                                    <Space>
                                        <Text type="secondary">사용 가능 행 수: </Text>
                                        <Text strong>{maxRows}</Text>
                                    </Space>
                                </Space>
                            </Col>
                            <Col>
                                <Space size="small">
                                    <Button
                                        type="primary"
                                        icon={<SearchOutlined />}
                                        onClick={handleSearch}
                                        loading={loading}
                                        style={{ minWidth: '100px' }}
                                    >
                                        조회 시작
                                    </Button>
                                    <Button
                                        className="page-action-btn"
                                        icon={<ReloadOutlined />}
                                        onClick={handleReset}
                                    >
                                        초기화
                                    </Button>
                                    <Button
                                        className="history-btn"
                                        icon={<HistoryOutlined />}
                                        onClick={() => {
                                            setShowHistoryModal(true);
                                            fetchHistory();
                                        }}
                                    >
                                        기록
                                    </Button>
                                    <Button
                                        className="excel-button"
                                        icon={<DownloadOutlined />}
                                        onClick={handleExportExcel}
                                        title="엑셀 다운로드"
                                    />
                                </Space>
                            </Col>
                        </Row>
                    </Card>
                </div>

                <div className="page-content-inner">
                    <div className="naver-rank-tracking-table-wrapper">
                        <div className="naver-rank-tracking-table">
                            <HotTable
                                ref={hotRef}
                                data={tableData}
                                colHeaders={colHeaders}
                                contextMenu={{
                                    items: {
                                        undo: {
                                            name: '되돌리기',
                                            hidden: function () {
                                                return !this.getPlugin(
                                                    'undoRedo'
                                                )?.isUndoAvailable();
                                            },
                                        },
                                        redo: {
                                            name: '다시 실행하기',
                                            hidden: function () {
                                                return !this.getPlugin(
                                                    'undoRedo'
                                                )?.isRedoAvailable();
                                            },
                                        },
                                        copy: { name: '복사' },
                                        cut: { name: '잘라내기' },
                                    },
                                }}
                                rowHeaders={true}
                                stretchH="all"
                                height="100%"
                                width="100%"
                                colWidths={colWidths}
                                manualColumnResize={true}
                                afterColumnResize={(newSize, column) => {
                                    setColWidths(prev => {
                                        const next = [...prev];
                                        next[column] = newSize;
                                        saveColWidths(next);
                                        return next;
                                    });
                                }}
                                cells={(row, col) => {
                                    const isCenter = col === 5 || col === 10 || col === 14;
                                    const base =
                                        col <= 3
                                            ? { readOnly: false, className: 'required-column' }
                                            : {
                                                  readOnly: true,
                                                  renderer:
                                                      col === 14
                                                          ? hotStatusPillRenderer
                                                          : undefined,
                                              };
                                    return {
                                        ...base,
                                        className: [base.className, isCenter ? 'htCenter' : null]
                                            .filter(Boolean)
                                            .join(' '),
                                    };
                                }}
                                beforeChange={(changes, source) => {
                                    if (!changes) return;
                                    const isPaste = source === 'CopyPaste.paste';
                                    if (isPaste) {
                                        const maxRowTouched = Math.max(
                                            ...changes.map(([row]) => row)
                                        );
                                        if (maxRowTouched + 1 > maxRows) {
                                            message.warning(
                                                `최대 ${maxRows}행까지만 입력할 수 있습니다.`
                                            );
                                            return false;
                                        }
                                    }
                                }}
                                licenseKey="non-commercial-and-evaluation"
                            />
                        </div>
                    </div>
                </div>

                <Modal
                    title="조회 기록"
                    open={showHistoryModal}
                    onCancel={() => setShowHistoryModal(false)}
                    centered
                    mask={{ blur: true }}
                    footer={[
                        <Button key="close" onClick={() => setShowHistoryModal(false)}>
                            닫기
                        </Button>,
                    ]}
                    width={800}
                    className="history-modal"
                    bodyStyle={{ maxHeight: '600px', overflowY: 'auto' }}
                >
                    {!historyLoaded ? (
                        <div style={{ textAlign: 'center', padding: '40px' }}>
                            <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
                        </div>
                    ) : historyData.length === 0 ? (
                        <Empty description="조회 기록이 없습니다." />
                    ) : (
                        <>
                            <List
                                dataSource={historyData}
                                renderItem={item => {
                                    const parts = {};
                                    (item.input || '').split('|').forEach(pair => {
                                        const [key, value] = pair.split('=');
                                        if (key) parts[key] = value;
                                    });
                                    return (
                                        <List.Item
                                            actions={[
                                                <Button
                                                    key="delete"
                                                    danger
                                                    size="small"
                                                    icon={<DeleteOutlined />}
                                                    onClick={() => handleDelete(item.id)}
                                                >
                                                    삭제
                                                </Button>,
                                            ]}
                                        >
                                            <List.Item.Meta
                                                title={
                                                    <Space>
                                                        <Text strong>
                                                            키워드: {parts.keyword || '-'}
                                                        </Text>
                                                        <Divider type="vertical" />
                                                        <Text>
                                                            포함키워드: {parts.include || '-'}
                                                        </Text>
                                                    </Space>
                                                }
                                                description={
                                                    <div>
                                                        {(parts.url1 || parts.url2) && (
                                                            <div style={{ marginBottom: '4px' }}>
                                                                <Text
                                                                    type="secondary"
                                                                    style={{
                                                                        fontSize: '12px',
                                                                        wordBreak: 'break-all',
                                                                    }}
                                                                >
                                                                    판매처URL: {parts.url1 || '-'}
                                                                    {parts.url2 ? (
                                                                        <>
                                                                            <br />
                                                                            원부URL: {parts.url2}
                                                                        </>
                                                                    ) : null}
                                                                </Text>
                                                            </div>
                                                        )}
                                                        <Text
                                                            type="secondary"
                                                            style={{ fontSize: '12px' }}
                                                        >
                                                            {item.updated_at || item.searched_at}
                                                        </Text>
                                                    </div>
                                                }
                                            />
                                        </List.Item>
                                    );
                                }}
                            />
                            {hasMore && historyData.length > 0 && (
                                <div
                                    style={{
                                        textAlign: 'center',
                                        marginTop: '16px',
                                    }}
                                >
                                    <Button
                                        onClick={() => fetchHistory(offset, true)}
                                        loading={fetching}
                                    >
                                        더보기
                                    </Button>
                                </div>
                            )}
                        </>
                    )}
                </Modal>
            </div>
            <KakaoFloatButton />
        </ProtectedMenuPage>
    );
};
export { KeywordRankVolumeCheck };
